import { useState, useEffect } from "react";
import useGlobalStore from "../stores/globalStore";
import useGetNetworks from "./useGetNetworks";
import useGetBroadbands from "./useGetBroadbands";
import useGetBroadbandEquipment from "./useGetBroadbandEquipment";
import useGetTvEquipment from "./useGetTvEquipment";
import useGetSecurity from "./useGetSecurity";
import useGetTvChannelPackages from "./useGetTvChannelPackages";
import useGetTvChannels from "./useGetTvChannels";
import useGetShippingOptions from "./useGetShippingOptions";

export default function useGetData() {
  //Store
  const selectedNetwork = useGlobalStore((state) => state.selectedNetwork);
  const selectedBroadband = useGlobalStore((state) => state.selectedBroadband);
  const selectedTvBasePackage = useGlobalStore((state) => state.selectedTvBasePackage);
  const selectedTvPackages = useGlobalStore((state) => state.selectedTvPackages);
  const selectedBroadbandEquipment = useGlobalStore((state) => state.selectedBroadbandEquipment);
  const selectedTvEquipment = useGlobalStore((state) => state.selectedTvEquipment);
  const selectedSecurity = useGlobalStore((state) => state.selectedSecurity);
  const setShippingOptions = useGlobalStore((state) => state.setShippingOptions);
  const setSelectedShipping = useGlobalStore((state) => state.setSelectedShipping);

  // State to manage the data, loading, and error
  const [data, setData] = useState([]);
  const [flexChannels, setFlexChannels] = useState([]);
  const [isPending, setIsPending] = useState(true);
  const [error, setError] = useState(null);

  //Hooks
  const { getNetworks } = useGetNetworks();
  const { getBroadbands } = useGetBroadbands();
  const { getTvChannelPackages } = useGetTvChannelPackages();
  const { getTvChannels } = useGetTvChannels();
  const { getBroadbandEquipment } = useGetBroadbandEquipment();
  const { getTvEquipment } = useGetTvEquipment();
  const { getSecurity } = useGetSecurity();
  const { getShippingOptions } = useGetShippingOptions();

  useEffect(() => {
    // Function to fetch network data
    const fetchData = async () => {
      try {
        //Get data
        const networks = await getNetworks();
        const broadbands = await getBroadbands();
        const tvChannels = await getTvChannels();
        const tvChannelPackages = await getTvChannelPackages(tvChannels);
        const broadbandEquipment = await getBroadbandEquipment();
        const tvEquipment = await getTvEquipment();
        const security = await getSecurity();
        const shippingOptions = await getShippingOptions();

        setShippingOptions(shippingOptions);
        setSelectedShipping(shippingOptions[0]);

        const populatedNetworks = [
          selectedNetwork,
          ...networks.map((network, index) => ({
            id: network.id,
            slug: network.slug,
            type: network.type,
            title: network.title,
            image: network.image,
            bredband: [
              selectedBroadband,
              ...network.bredband?.map((networkBroadband) => {
                return broadbands?.find((broadband) => {
                  return broadband.id === networkBroadband;
                });
              }),
            ],
            bredband_tillbehor: [
              ...selectedBroadbandEquipment,
              ...network.bredband_tillbehor?.map((networkBroadbandEquipment) => {
                return broadbandEquipment?.find((broadbandEquip) => {
                  return broadbandEquip.id === networkBroadbandEquipment;
                });
              }),
            ],
            tv_kanal_bas_paket: [
              selectedTvBasePackage,

              tvChannelPackages?.find((tvChanelPackage) => {
                if (tvChanelPackage.type === "tv_kanal_bas_paket") {
                  return tvChanelPackage;
                }
              }),
            ],
            tv_kanal_paket: [
              ...selectedTvPackages,
              ...network.tv_kanal_paket?.map((networkTvChannelPackages) => {
                return tvChannelPackages?.find((tvChanelPackage) => {
                  return tvChanelPackage.id === networkTvChannelPackages;
                });
              }),
            ],
            tv_tillbehor: [
              ...selectedTvEquipment,
              ...network.tv_tillbehor?.map((networkTvEquipment) => {
                return tvEquipment?.find((tvEquip) => {
                  return tvEquip.id === networkTvEquipment;
                });
              }),
            ],

            sakerhet: [
              selectedSecurity,
              ...network.sakerhet?.map((networkSecurity) => {
                return security?.find((sec) => {
                  return sec.id === networkSecurity;
                });
              }),
            ],
          })),
        ];

        setData(populatedNetworks);
        setFlexChannels(tvChannels);
      } catch (error) {
        setError(error);
      } finally {
        setIsPending(false);
      }
    };

    // Call the fetchData function
    fetchData();
  }, []); // Empty dependency array to run the effect only once

  // Return relevant data for the component using this hook
  return {
    isPending,
    error,
    data,
    flexChannels,
  };
}
